import { useRef, useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";

import { useDataSource } from "@camberi/firecms";
import versionsCollection from "../Collections/versionsCollection";
import { PreviewImage } from "./PreviewImage";

const getCoconutAssetIds = (coconutJobs: any) => {
	return Object.entries(coconutJobs || {})
		.filter(([, job]: any) => {
			return Boolean(job?.preview || job?.renditions);
		})
		.map(([uploadId]) => uploadId);
};

const getImageAssetIds = (imageJobs: any) => {
	return Object.entries(imageJobs || {})
		.filter(([, job]: any) => {
			return Boolean(job?.preview || job?.renditions);
		})
		.map(([uploadId]) => uploadId);
};

const getPreviewAssetIds = (versionValues: any) => {
	const fioAssets = versionValues?.fio_asset_ids || {};
	const fioAssetIds = Object.keys(fioAssets);

	const coconutJobs = versionValues?.coconut_jobs || {};
	const coconutAssetIds = getCoconutAssetIds(coconutJobs);
	const fallbackCoconutAssetIds = coconutAssetIds.length ? coconutAssetIds : Object.keys(coconutJobs);

	const imageJobs = versionValues?.image_jobs || {};
	const imageAssetIds = getImageAssetIds(imageJobs);
	const fallbackImageAssetIds = imageAssetIds.length ? imageAssetIds : Object.keys(imageJobs);

	const curPreviewFav = versionValues?.cur_preview_fav || "";

	if (curPreviewFav && coconutJobs?.[curPreviewFav] && !fioAssets?.[curPreviewFav]) {
		return fallbackCoconutAssetIds;
	}

	if (curPreviewFav && imageJobs?.[curPreviewFav] && !fioAssets?.[curPreviewFav] && !coconutJobs?.[curPreviewFav]) {
		return fallbackImageAssetIds;
	}

	if (fioAssetIds.length) {
		return fioAssetIds;
	}

	if (fallbackCoconutAssetIds.length) {
		return fallbackCoconutAssetIds;
	}

	return fallbackImageAssetIds;
};

export default function PermissionListPreviewImage({ versionId }: { versionId: string }) {
	const [version, setVersion] = useState<any>({});
	const versionListener = useRef<any>();

	const dataSource = useDataSource();

	const onVersionsUpdate = (evt: any) => {
		setVersion((prevState: any) => ({ ...prevState, ...evt }));
	};

	useEffect(() => {
		if (typeof versionListener.current === "undefined" && dataSource.listenEntity && versionId) {
			versionListener.current = dataSource.listenEntity({
				path: versionsCollection.path,
				entityId: versionId,
				collection: versionsCollection,
				onUpdate: (evt) => onVersionsUpdate(evt),
			});

			return () => {
				versionListener.current();
				versionListener.current = undefined;
			};
		}
	}, [dataSource, versionId]);

	const [tooltipIsOpen, setTooltipIsOpen] = useState<boolean>(false);

	const previewAssetIds = useMemo(() => {
		return getPreviewAssetIds(version?.values || {});
	}, [version?.values]);

	const numberOfAsset = previewAssetIds.length;

	return numberOfAsset > 0 ? (
		<Tooltip
			onClick={() => setTooltipIsOpen(false)}
			onOpen={() => setTooltipIsOpen(true)}
			onClose={() => setTooltipIsOpen(false)}
			arrow
			componentsProps={{
				tooltip: {
					sx: {
						m: 0,
						p: 0,
						bgcolor: "transparent",
						"& .MuiTooltip-arrow": {
							color: "background.paper",
						},
					},
				},
			}}
			open={tooltipIsOpen}
			title={
				<PreviewImage
					version={version}
					assetId="fav"
					autoplay
					is_visible={tooltipIsOpen}
					img_type="thumb_scrub"
					onclick={() => setTooltipIsOpen(false)}
					sx={{
						width: "240px",
						height: "135px",
						cursor: "pointer",
						borderRadius: "5px",
						overflow: "hidden",
						zIndex: 0,
					}}
				/>
			}
			placement="left"
		>
			<Badge
				invisible={numberOfAsset < 2}
				sx={{
					cursor: "pointer",
					"& .MuiBadge-badge": {
						backgroundColor: "rgba(0,0,0,.75)",
					},
				}}
				badgeContent={
					<Typography variant="body2" color="text.primary" align="center">
						{numberOfAsset}
					</Typography>
				}
				color="primary"
			>
				<PreviewImage
					version={version}
					assetId="fav"
					img_type="thumb"
					onclick={() => {
						setTooltipIsOpen(false);
					}}
					sx={{
						cursor: "pointer",
						borderRadius: "5px",
						overflow: "hidden",
						width: "72px",
						height: "40px",
						zIndex: 0,
					}}
				/>
			</Badge>
		</Tooltip>
	) : null;
}
